// src/services/session.js

import {
    getAddinState,
    saveAddinState,
    clearAddinState
} from "./addinStorage.js";

import { getExtensionTemplates } from "./createDraft.js";
import { REGIONS } from "./regions.js";

const API_KEY = import.meta.env.VITE_SNOOOZ_API_KEY;


export async function getCachedSessionBundle() {
    return await getAddinState();
}

export async function logoutSession() {
    await clearAddinState();
}

export function getOfficeMailboxProfile() {
    const office = window.Office;
    const mailbox = office?.context?.mailbox;
    const userProfile = mailbox?.userProfile;

    const diagnostics = mailbox?.diagnostics || {};
    const host = diagnostics?.hostName || "";
    const platform = diagnostics?.platform || "";

    const email = String(userProfile?.emailAddress || "")
        .toLowerCase()
        .trim();

    const displayName = String(userProfile?.displayName || "").trim();

    console.log("Snoooz Outlook profile debug:", {
        hasOffice: !!office,
        hasContext: !!office?.context,
        hasMailbox: !!mailbox,
        hasUserProfile: !!userProfile,
        email,
        displayName,
        host,
        platform,
        diagnostics
    });

    return {
        email,
        displayName,
        firstName: getFirstName(displayName),
        lastName: getLastName(displayName),
        timeZone: userProfile?.timeZone || "",
        host,
        platform
    };
}

export async function resolveOrCreateOutlookSession(region = "us", manualEmail = "") {
    const regionConfig = REGIONS[region];

    if (!regionConfig?.sessionUrl) {
        throw new Error("Missing Snoooz region configuration.");
    }

    const profile = getOfficeMailboxProfile();

    const fallbackEmail = String(manualEmail || "")
        .toLowerCase()
        .trim();

    const finalEmail = profile.email || fallbackEmail;

    if (!finalEmail) {
        const err = new Error("EMAIL_REQUIRED");
        err.code = "EMAIL_REQUIRED";
        throw err;
    }

    if (!isValidEmail(finalEmail)) {
        const err = new Error("Please enter a valid email address.");
        err.code = "INVALID_EMAIL";
        throw err;
    }

    console.log("Snoooz session request starting:", {
        region,
        hasSessionUrl: !!regionConfig.sessionUrl,
        hasApiKey: !!API_KEY,
        officeEmail: profile.email,
        manualEmail: fallbackEmail,
        finalEmail,
        host: profile.host,
        platform: profile.platform
    });

    let response;

    try {
        response = await fetch(regionConfig.sessionUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "x-api-key": API_KEY
            },
            body: JSON.stringify({
                email: finalEmail,
                displayName: profile.displayName,
                firstName: profile.firstName,
                lastName: profile.lastName,
                timeZone: profile.timeZone,
                source: profile.email ? "outlook_addin" : "outlook_addin_manual_email"
            })
        });
    } catch (err) {
        const networkErr = new Error(
            `Network error calling Snoooz. Please check whether your network allows access to Snoooz. Details: ${err.message || err}`
        );
        networkErr.code = "NETWORK_ERROR";
        throw networkErr;
    }

    const data = await response.json().catch(() => ({}));

    console.log("Snoooz session response:", {
        ok: response.ok,
        status: response.status,
        data
    });

    if (!response.ok) {
        const err = new Error(data?.message || data?.error || `Could not connect to Snoooz. Status ${response.status}.`);
        err.code = data?.error;
        err.region = data?.region;
        err.status = response.status;
        err.data = data;
        throw err;
    }

    const session = normalizeSession(data, {
        ...profile,
        email: finalEmail
    });

    if (!session.companyId || !session.userId) {
        throw new Error("Snoooz session is missing companyId or userId.");
    }

    let templates = [];

    try {
        templates = await getExtensionTemplates(session, region);
    } catch (err) {
        console.warn("Could not load templates:", err);
    }

    const bundle = {
        state: {
            mode: "loggedIn",
            region,
            session,
            savedAt: Date.now()
        },
        templates
    };

    await saveAddinState(bundle);

    return bundle;
}

function normalizeSession(data, profile) {
    const raw = data?.session || data?.user || data || {};

    return {
        ...raw,
        companyId: raw.companyId || data?.companyId || "",
        workspaceId: raw.workspaceId || data?.workspaceId || raw.companyId || data?.companyId || "",
        userId: raw.userId || raw.id || data?.userId || data?.uid || "",
        email: raw.email || profile.email || "",
        fullName: raw.fullName || raw.name || profile.displayName || "",
        firstName: raw.firstName || raw.profile?.firstName || profile.firstName || "",
        lastName: raw.lastName || raw.profile?.lastName || profile.lastName || "",
        bookingLink: raw.bookingLink || raw.profile?.bookingLink || ""
    };
}

function getFirstName(fullName = "") {
    const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
    return parts[0] || "";
}

function getLastName(fullName = "") {
    const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
    return parts.slice(1).join(" ");
}

function isValidEmail(value = "") {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export async function requestOutlookSigninCode(region = "us", email = "") {
    const regionConfig = REGIONS[region];

    if (!regionConfig?.requestCodeUrl) {
        throw new Error("Missing Snoooz code request URL.");
    }

    const cleanEmailValue = String(email || "").toLowerCase().trim();

    if (!isValidEmail(cleanEmailValue)) {
        const err = new Error("Please enter a valid email address.");
        err.code = "INVALID_EMAIL";
        throw err;
    }

    const response = await fetch(regionConfig.requestCodeUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY
        },
        body: JSON.stringify({
            email: cleanEmailValue,
            region
        })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        const err = new Error(
            data?.message ||
            data?.error ||
            `Could not send sign-in code. Status ${response.status}`
        );
        err.code = data?.error;
        err.status = response.status;
        err.data = data;

        console.error("requestOutlookSigninCode failed:", {
            status: response.status,
            data
        });

        throw err;
    }

    return {
        otpId: data.otpId,
        email: cleanEmailValue,
        expiresInSeconds: data.expiresInSeconds || 600
    };
}

export async function verifyOutlookSigninCode({
    region = "us",
    email = "",
    otpId = "",
    code = ""
}) {
    const regionConfig = REGIONS[region];

    if (!regionConfig?.verifyCodeUrl) {
        throw new Error("Missing Snoooz code verification URL.");
    }

    const profile = getOfficeMailboxProfile();
    const cleanEmailValue = String(email || "").toLowerCase().trim();

    const response = await fetch(regionConfig.verifyCodeUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY
        },
        body: JSON.stringify({
            otpId,
            code: String(code || "").trim(),
            email: cleanEmailValue,
            displayName: profile.displayName,
            firstName: profile.firstName,
            lastName: profile.lastName,
            timeZone: profile.timeZone,
            source: "outlook_addin_otp"
        })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        const err = new Error(data?.message || data?.error || "Could not verify sign-in code.");
        err.code = data?.error;
        err.status = response.status;
        err.data = data;
        throw err;
    }

    const session = normalizeSession(data, {
        ...profile,
        email: cleanEmailValue
    });

    if (!session.companyId || !session.userId) {
        throw new Error("Snoooz session is missing companyId or userId.");
    }

    const bundle = {
        state: {
            mode: "loggedIn",
            region,
            session,
            savedAt: Date.now()
        },
        templates: Array.isArray(data?.templates) ? data.templates : []
    };

    await saveAddinState(bundle);

    return bundle;
}